/**
 * Release Category Precedence Rules
 * 
 * Handles overlapping keywords and determines final category assignment
 * when a release matches multiple categories.
 */

import { ReleaseCategory, CATEGORY_PRECEDENCE, CategorizationResult } from '../types/release'
import { findAllMatchingKeywords, calculateMatchConfidence } from './releaseKeywordMatcher'

// ============================================================================
// PRECEDENCE RULE TYPES
// ============================================================================

/**
 * Category match with confidence and context
 */
interface CategoryMatch {
  category: ReleaseCategory
  keywords: string[]
  confidence: number
  weight: number
}

/**
 * Precedence rule configuration
 */
interface PrecedenceRule {
  /** Rule name for debugging */
  name: string
  /** Rule priority (higher = more important) */
  priority: number
  /** Function to evaluate the rule */
  evaluate: (matches: CategoryMatch[], text: string) => ReleaseCategory | null
  /** Description of what this rule does */
  description: string
}

// ============================================================================
// PRECEDENCE RULES
// ============================================================================

/**
 * Rule 1: Exact keyword priority
 * Prioritizes categories with exact, specific keyword matches
 */
const exactKeywordRule: PrecedenceRule = {
  name: 'exactKeyword',
  priority: 100,
  description: 'Prioritizes categories with exact, specific keyword matches',
  evaluate: (matches: CategoryMatch[], text: string) => {
    // Look for highly specific keywords that should take precedence
    const specificKeywords = {
      [ReleaseCategory.OSS]: ['OSS'],
      [ReleaseCategory.SOFTWARE]: ['Software', 'k8s'],
      [ReleaseCategory.CLOUD]: ['RCP', 'CAPI', 'SM'], // Added SM as specific keyword
      [ReleaseCategory.AZURE]: ['Azure'] // Azure now has specific keyword
    }
    
    for (const match of matches) {
      const specific = specificKeywords[match.category]
      const hasSpecificMatch = specific.some(keyword => 
        match.keywords.includes(keyword)
      )
      
      if (hasSpecificMatch && match.confidence > 0.5) {
        return match.category
      }
    }
    
    return null
  }
}

/**
 * Rule 2: Confidence threshold rule
 * Selects category with highest confidence if significantly higher than others
 */
const confidenceThresholdRule: PrecedenceRule = {
  name: 'confidenceThreshold',
  priority: 90,
  description: 'Selects category with highest confidence if significantly higher than others',
  evaluate: (matches: CategoryMatch[]) => {
    if (matches.length < 2) return null
    
    // Sort by confidence descending
    const sorted = [...matches].sort((a, b) => b.confidence - a.confidence)
    const highest = sorted[0]
    const secondHighest = sorted[1]
    
    // If highest confidence is significantly higher (20% threshold)
    if (highest.confidence - secondHighest.confidence > 0.2) {
      return highest.category
    }
    
    return null
  }
}

/**
 * Rule 3: Multiple keyword bonus
 * Prioritizes categories with multiple keyword matches
 */
const multipleKeywordRule: PrecedenceRule = {
  name: 'multipleKeyword',
  priority: 80,
  description: 'Prioritizes categories with multiple keyword matches',
  evaluate: (matches: CategoryMatch[]) => {
    const multipleMatches = matches.filter(match => match.keywords.length > 1)
    
    if (multipleMatches.length === 1) {
      return multipleMatches[0].category
    }
    
    if (multipleMatches.length > 1) {
      // Return the one with most keywords
      const mostKeywords = multipleMatches.reduce((prev, current) => 
        current.keywords.length > prev.keywords.length ? current : prev
      )
      return mostKeywords.category
    }
    
    return null
  }
}

/**
 * Rule 4: Default precedence order
 * Falls back to predefined category precedence
 */
const defaultPrecedenceRule: PrecedenceRule = {
  name: 'defaultPrecedence',
  priority: 10,
  description: 'Falls back to predefined category precedence order',
  evaluate: (matches: CategoryMatch[]) => {
    // Use the predefined precedence order (Software > Cloud > Azure > OSS)
    for (const category of [...CATEGORY_PRECEDENCE].reverse()) {
      if (matches.some(match => match.category === category)) {
        return category
      }
    }
    return null
  }
}

/**
 * All precedence rules in priority order
 */
const PRECEDENCE_RULES: PrecedenceRule[] = [
  exactKeywordRule,
  confidenceThresholdRule,
  multipleKeywordRule,
  defaultPrecedenceRule
].sort((a, b) => b.priority - a.priority)

// ============================================================================
// PRECEDENCE RESOLUTION FUNCTIONS
// ============================================================================

/**
 * Resolve category conflicts using precedence rules
 */
export function resolveCategoryConflicts(
  text: string,
  options: {
    exactWord?: boolean
    caseSensitive?: boolean
    minConfidence?: number
    debugMode?: boolean
  } = {}
): CategorizationResult {
  const { minConfidence = 0.3, debugMode = false } = options
  
  // Find all matching keywords across categories
  const allMatches = findAllMatchingKeywords(text, options)
  
  // Convert to CategoryMatch objects
  const categoryMatches: CategoryMatch[] = []
  
  for (const [category, keywords] of Object.entries(allMatches)) {
    if (keywords.length > 0) {
      const confidence = calculateMatchConfidence(keywords, keywords.length, text.length)
      
      if (confidence >= minConfidence) {
        categoryMatches.push({
          category: category as ReleaseCategory,
          keywords,
          confidence,
          weight: calculateCategoryWeight(category as ReleaseCategory, keywords)
        })
      }
    }
  }
  
  // If no matches, return failure
  if (categoryMatches.length === 0) {
    return {
      success: false,
      matchedKeywords: [],
      confidence: 0,
      reason: 'No keywords matched any category'
    }
  }
  
  // If only one match, return it
  if (categoryMatches.length === 1) {
    const match = categoryMatches[0]
    return {
      category: match.category,
      matchedKeywords: match.keywords,
      success: true,
      confidence: match.confidence,
      reason: `Single category match: ${match.category}`
    }
  }
  
  // Multiple matches - apply precedence rules
  const debugInfo: Array<{ rule: string; result: ReleaseCategory | null; reason: string }> = []
  
  for (const rule of PRECEDENCE_RULES) {
    const result = rule.evaluate(categoryMatches, text)
    
    if (debugMode) {
      debugInfo.push({
        rule: rule.name,
        result,
        reason: rule.description
      })
    }
    
    if (result) {
      const finalMatch = categoryMatches.find(m => m.category === result)!
      return {
        category: result,
        matchedKeywords: finalMatch.keywords,
        success: true,
        confidence: finalMatch.confidence,
        reason: `Resolved by rule: ${rule.name} - ${rule.description}`,
        ...(debugMode && { debugInfo })
      }
    }
  }
  
  // Fallback - should not happen due to defaultPrecedenceRule
  const fallbackMatch = categoryMatches[0]
  return {
    category: fallbackMatch.category,
    matchedKeywords: fallbackMatch.keywords,
    success: true,
    confidence: fallbackMatch.confidence,
    reason: 'Fallback to first match (precedence rules failed)',
    ...(debugMode && { debugInfo })
  }
}

/**
 * Calculate weight for a category based on keyword specificity
 */
function calculateCategoryWeight(category: ReleaseCategory, keywords: string[]): number {
  // Base weight
  let weight = 1.0
  
  // Keyword specificity bonuses
  const specificityBonus = {
    'OSS': 2.0,      // Very specific
    'Software': 1.8,  // Specific
    'k8s': 1.8,      // Specific
    'RCP': 1.6,      // Moderately specific
    'CAPI': 1.6,     // Moderately specific
    'SM': 1.6,       // Moderately specific (Cloud-specific)
    'Cross': 0.8,    // Generic (appears in multiple categories)
    'Cloud': 1.2,    // Somewhat specific
    'cloud': 1.2     // Somewhat specific
  }
  
  // Apply bonuses for specific keywords
  for (const keyword of keywords) {
    const bonus = specificityBonus[keyword as keyof typeof specificityBonus] || 1.0
    weight *= bonus
  }
  
  // Category-specific adjustments
  switch (category) {
    case ReleaseCategory.OSS:
      weight *= 1.2 // Boost OSS as it's most specific
      break
    case ReleaseCategory.AZURE:
      weight *= 0.9 // Slight penalty as Azure keywords overlap heavily
      break
  }
  
  return weight
}

/**
 * Get detailed precedence analysis for debugging
 */
export function analyzePrecedence(text: string): {
  text: string
  allMatches: Record<ReleaseCategory, string[]>
  categoryMatches: CategoryMatch[]
  finalResult: CategorizationResult
  ruleEvaluations: Array<{ rule: string; result: ReleaseCategory | null; description: string }>
} {
  const allMatches = findAllMatchingKeywords(text)
  
  const categoryMatches: CategoryMatch[] = []
  for (const [category, keywords] of Object.entries(allMatches)) {
    if (keywords.length > 0) {
      const confidence = calculateMatchConfidence(keywords, keywords.length, text.length)
      categoryMatches.push({
        category: category as ReleaseCategory,
        keywords,
        confidence,
        weight: calculateCategoryWeight(category as ReleaseCategory, keywords)
      })
    }
  }
  
  const ruleEvaluations = PRECEDENCE_RULES.map(rule => ({
    rule: rule.name,
    result: rule.evaluate(categoryMatches, text),
    description: rule.description
  }))
  
  const finalResult = resolveCategoryConflicts(text, { debugMode: true })
  
  return {
    text,
    allMatches,
    categoryMatches,
    finalResult,
    ruleEvaluations
  }
}
